import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useParams, useLocation, Link } from 'react-router-dom';
import { authService } from '../../services/auth/authService';
import { DashboardHeader } from '../../components/layout/DashboardHeader/DashboardHeader';
import { DashboardSidebar } from '../../components/layout/DashboardSidebar/DashboardSidebar';
import { createSidebarItems } from '../../components/layout/DashboardSidebar/sidebarItems';
import {
  aiRequestsService,
  ConsultationRequestWithDetails,
} from '../../services/ai-requests/aiRequestsService';
import { formatDateTime } from '../../utils/dateUtils';
import { getAIRequestStatusBadgeClass, getAIRequestStatusLabel } from '../../utils/statusUtils';
import {
  User,
  Brain,
  Zap,
  ClipboardList,
  Pill,
  Link2,
  FlaskConical,
  Check,
  Bot,
} from 'lucide-react';
import './ConsultationDetailsPage.css';

const ConsultationDetailsPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const [consultation, setConsultation] = useState<ConsultationRequestWithDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [doctorNotes, setDoctorNotes] = useState('');
  const [generatingSummary, setGeneratingSummary] = useState(false);
  const [processingStatus, setProcessingStatus] = useState<number | null>(null);
  const [prescriptions, setPrescriptions] = useState<Array<{
    id: string;
    medication_name: string;
    dosage: string;
    frequency: string;
    instructions: string;
  }>>([]);
  const [labTests, setLabTests] = useState<Array<{
    id: string;
    test_name: string;
    reason: string;
    instructions: string;
  }>>([]);
  const [expandedSections, setExpandedSections] = useState<{
    aiSummary: boolean;
  }>({
    aiSummary: false,
  });
  const [activeTab, setActiveTab] = useState<'aiResponse' | 'chatHistory'>('aiResponse');
  const prescriptionsEndRef = useRef<HTMLDivElement>(null);
  const labTestsEndRef = useRef<HTMLDivElement>(null);

  const loadConsultationDetails = useCallback(async () => {
    if (!id) return;

    try {
      setLoading(true);
      setError(null);
      const data = await aiRequestsService.getConsultationDetails(parseInt(id));
      setConsultation(data);
      setDoctorNotes(data.doctor_notes || '');
    } catch (err: any) {
      console.error('Failed to load consultation details:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to load consultation details');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      loadConsultationDetails();
    }
  }, [id, loadConsultationDetails]);

  const handleLogout = () => {
    authService.clearTokens();
    navigate('/login');
  };

  const handleGoToProfile = () => {
    navigate('/profile');
  };

  const handleAcceptRequest = async () => {
    if (!id || !consultation) return;

    try {
      setProcessingStatus(consultation.id);
      const updatedRequest = await aiRequestsService.updateRequestStatus(parseInt(id), 'pending');
      setConsultation({
        ...consultation,
        status: updatedRequest.status,
      });
    } catch (err: any) {
      console.error('Failed to accept request:', err);
      alert(err.response?.data?.detail || 'Failed to accept request');
    } finally {
      setProcessingStatus(null);
    }
  };

  const handleRejectRequest = async () => {
    if (!id || !consultation) return;

    if (!window.confirm('Are you sure you want to reject this AI request?')) {
      return;
    }

    try {
      setProcessingStatus(consultation.id);
      const updatedRequest = await aiRequestsService.updateRequestStatus(parseInt(id), 'rejected');
      setConsultation({
        ...consultation,
        status: updatedRequest.status,
      });
    } catch (err: any) {
      console.error('Failed to reject request:', err);
      alert(err.response?.data?.detail || 'Failed to reject request');
    } finally {
      setProcessingStatus(null);
    }
  };

  const handleGenerateSummary = async () => {
    if (!id || !consultation) return;

    // Only allow generating summary if request is pending (accepted) or completed
    const statusLower = consultation.status?.toLowerCase() || '';
    if (statusLower !== 'pending' && statusLower !== 'completed') {
      alert('Please accept the request before generating a summary.');
      return;
    }

    // Prevent generating summary multiple times
    if (consultation.ai_summary) {
      alert('AI summary has already been generated for this consultation.');
      return;
    }

    try {
      setGeneratingSummary(true);
      const updatedRequest = await aiRequestsService.generateSummary(parseInt(id));
      // Update the consultation with the new AI summary
      setConsultation({
        ...consultation,
        ai_summary: updatedRequest.ai_summary,
      });
      // Expand the summary section to show the generated content
      setExpandedSections((prev) => ({
        ...prev,
        aiSummary: true,
      }));
    } catch (err: any) {
      console.error('Failed to generate summary:', err);
      alert(err.response?.data?.detail || 'Failed to generate AI summary');
    } finally {
      setGeneratingSummary(false);
    }
  };

  const handleAddPrescription = () => {
    const newPrescription = {
      id: `prescription-${Date.now()}`,
      medication_name: '',
      dosage: '',
      frequency: '',
      instructions: '',
    };
    setPrescriptions([...prescriptions, newPrescription]);
    // Scroll to the newly added prescription after state update
    setTimeout(() => {
      prescriptionsEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, 100);
  };

  const handleDeletePrescription = (prescriptionId: string) => {
    setPrescriptions(prescriptions.filter((p) => p.id !== prescriptionId));
  };

  const handleUpdatePrescription = (
    prescriptionId: string,
    field: 'medication_name' | 'dosage' | 'frequency' | 'instructions',
    value: string
  ) => {
    setPrescriptions(
      prescriptions.map((p) => (p.id === prescriptionId ? { ...p, [field]: value } : p))
    );
  };

  const handleAddLabTest = () => {
    const newLabTest = {
      id: `labtest-${Date.now()}`,
      test_name: '',
      reason: '',
      instructions: '',
    };
    setLabTests([...labTests, newLabTest]);
    // Scroll to the newly added lab test after state update
    setTimeout(() => {
      labTestsEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, 100);
  };

  const handleDeleteLabTest = (labTestId: string) => {
    setLabTests(labTests.filter((t) => t.id !== labTestId));
  };

  const handleUpdateLabTest = (
    labTestId: string,
    field: 'test_name' | 'reason' | 'instructions',
    value: string
  ) => {
    setLabTests(
      labTests.map((t) => (t.id === labTestId ? { ...t, [field]: value } : t))
    );
  };

  const handleCompleteConsultation = async () => {
    if (!id || !consultation) return;

    try {
      // Build comprehensive response including prescriptions and lab tests
      let fullResponse = doctorNotes;

      // Add prescriptions to response
      const validPrescriptions = prescriptions.filter((p) => p.medication_name.trim() !== '');
      if (validPrescriptions.length > 0) {
        fullResponse += '\n\n## Prescribed Medications:\n';
        for (const prescription of validPrescriptions) {
          fullResponse += `• ${prescription.medication_name}`;
          if (prescription.dosage) {
            fullResponse += ` - ${prescription.dosage}`;
          }
          if (prescription.frequency) {
            fullResponse += ` (${prescription.frequency})`;
          }
          if (prescription.instructions) {
            fullResponse += `\n  Instructions: ${prescription.instructions}`;
          }
          fullResponse += '\n';
        }
      }

      // Add lab tests to response
      const validLabTests = labTests.filter((t) => t.test_name.trim() !== '');
      if (validLabTests.length > 0) {
        fullResponse += '\n\n## Recommended Lab Tests:\n';
        for (const test of validLabTests) {
          fullResponse += `• ${test.test_name}`;
          if (test.reason) {
            fullResponse += ` - ${test.reason}`;
          }
          if (test.instructions) {
            fullResponse += `\n  Instructions: ${test.instructions}`;
          }
          fullResponse += '\n';
        }
      }

      // Prepare prescriptions and lab tests data for the single API call
      const prescriptionsData = validPrescriptions.length > 0
        ? validPrescriptions.map((p) => ({
            medication_name: p.medication_name,
            dosage: p.dosage,
            frequency: p.frequency,
            instructions: p.instructions,
          }))
        : undefined;

      const labTestsData = validLabTests.length > 0
        ? validLabTests.map((t) => ({
            test_name: t.test_name,
            reason: t.reason,
            instructions: t.instructions,
          }))
        : undefined;

      // Update consultation status to completed with full response, prescriptions, and lab tests in one call
      await aiRequestsService.updateRequestStatus(
        parseInt(id),
        'completed',
        fullResponse,
        prescriptionsData,
        labTestsData
      );
      
      // Reload consultation details
      await loadConsultationDetails();
      
      alert('Consultation completed successfully!');
    } catch (err: any) {
      console.error('Failed to complete consultation:', err);
      alert(err.response?.data?.detail || 'Failed to complete consultation');
    }
  };


  const getPatientName = (): string => {
    if (consultation?.patient_name) return consultation.patient_name;
    if (consultation?.context) {
      try {
        const context = JSON.parse(consultation.context);
        return context.patient_name || 'Patient';
      } catch {
        return 'Patient';
      }
    }
    return 'Patient';
  };

  const sidebarItems = createSidebarItems(navigate, location.pathname);

  if (loading) {
    return (
      <div className="dashboard">
        <DashboardHeader onProfileClick={handleGoToProfile} onLogout={handleLogout} />
        <div className="dashboard-layout">
          <DashboardSidebar items={sidebarItems} />
          <div className="dashboard-container">
            <main className="dashboard-main-content">
              <div className="loading-message">Loading consultation details...</div>
            </main>
          </div>
        </div>
      </div>
    );
  }

  if (error || !consultation) {
    return (
      <div className="dashboard">
        <DashboardHeader onProfileClick={handleGoToProfile} onLogout={handleLogout} />
        <div className="dashboard-layout">
          <DashboardSidebar items={sidebarItems} />
          <div className="dashboard-container">
            <main className="dashboard-main-content">
              <div className="error-message">Error: {error || 'Consultation not found'}</div>
            </main>
          </div>
        </div>
      </div>
    );
  }

  const patientName = getPatientName();

  return (
    <div className="dashboard">
      <DashboardHeader onProfileClick={handleGoToProfile} onLogout={handleLogout} />

      <div className="dashboard-layout">
        <DashboardSidebar items={sidebarItems} />

        <div className="dashboard-container">
          <main className="dashboard-main-content">
            <div className="consultation-details-card">
            <nav className="detail-breadcrumb" aria-label="Breadcrumb">
              <Link to="/ai-requests" className="breadcrumb-link">AI Requests</Link>
              <span className="breadcrumb-sep" aria-hidden>›</span>
              <span className="breadcrumb-current">Consultation Details</span>
            </nav>
            {/* Header */}
            <div className="dashboard-section">
              <h1 className="consultation-title">Consultation Details</h1>
            </div>

            {/* Request Info */}
            <div className="dashboard-section">
              <div className="request-info-header">
                <div className="request-info-left">
                  <h2 className="request-by-title">Request by {patientName}</h2>
                  <div className="request-meta">
                    <span className={`status-badge-large ${getAIRequestStatusBadgeClass(consultation.status, false)}`}>
                      Status: {getAIRequestStatusLabel(consultation.status)}
                    </span>
                    <span className="normal-tag">Normal</span>
                  </div>
                  <div className="request-date">{formatDateTime(consultation.created_at)}</div>
                </div>
              </div>
            </div>

            {/* Patient Questions Section */}
            <div className="dashboard-section">
              <div className="section-header">
                <div className="section-title-with-icon">
                  <span className="section-icon" aria-hidden><User size={20} /></span>
                  <h3 className="section-title">Patient Questions</h3>
                </div>
              </div>
              <div className="section-content">
                {(consultation.request_type || '').toLowerCase() === 'verification' ? (
                  <p className="patient-question-text">
                    {consultation.user_question || 'No question provided'}
                  </p>
                ) : consultation.chat_messages && consultation.chat_messages.length > 0 ? (
                  <div className="questions-answers-list">
                    {(() => {
                      // Extract all Q&A pairs from chat messages sequentially
                      // Pattern: Assistant (question) -> User (answer) -> Assistant (question) -> User (answer) -> ...
                      const qaPairs: Array<{question: string, answer: string}> = [];
                      const messages = consultation.chat_messages || [];
                      
                      const looksLikeQuestion = (content: string): boolean => {
                        const t = (content || '').trim();
                        if (!t) return false;
                        const lower = t.toLowerCase();
                        if (t.endsWith('?')) return true;
                        return (
                          lower.startsWith('what') ||
                          lower.startsWith('when') ||
                          lower.startsWith('where') ||
                          lower.startsWith('how') ||
                          lower.startsWith('why') ||
                          lower.startsWith('can you') ||
                          lower.startsWith('could you') ||
                          lower.startsWith('do you') ||
                          lower.startsWith('did you') ||
                          lower.startsWith('have you') ||
                          lower.startsWith('are you')
                        );
                      };

                      let i = 0;
                      while (i < messages.length) {
                        const msg = messages[i];
                        
                        // Identify questions: assistant messages ending with '?' or starting with a question stem
                        const isQuestion = msg.role === 'assistant' && looksLikeQuestion(msg.content);
                        
                        if (isQuestion) {
                          // Look for the immediate next user message as the answer
                          // Skip any intermediate assistant messages (could be status updates)
                          let answerFound = false;
                          for (let j = i + 1; j < messages.length; j++) {
                            const nextMsg = messages[j];
                            if (nextMsg.role === 'user') {
                              qaPairs.push({
                                question: msg.content,
                                answer: nextMsg.content
                              });
                              answerFound = true;
                              i = j + 1; // Move past the answer
                              break;
                            } else if (nextMsg.role === 'assistant' && 
                                      looksLikeQuestion(nextMsg.content)) {
                              // Found another question before an answer - this question has no answer yet
                              qaPairs.push({
                                question: msg.content,
                                answer: ''
                              });
                              i = j; // Process the next question
                              break;
                            }
                          }
                          
                          if (!answerFound) {
                            // Question without answer yet
                            qaPairs.push({
                              question: msg.content,
                              answer: ''
                            });
                            i++;
                          }
                        } else {
                          i++;
                        }
                      }
                      
                      if (qaPairs.length > 0) {
                        return qaPairs.map((qa, idx) => (
                          <div key={idx} className="qa-item">
                            <div className="question-item">
                              <strong>Q:</strong> {qa.question}
                            </div>
                            {qa.answer && (
                              <div className="answer-item">
                                <strong>A:</strong> {qa.answer}
                              </div>
                            )}
                          </div>
                        ));
                      } else {
                        // Fallback to initial question
                        return (
                          <p className="patient-question-text">
                            {consultation.user_question || 'No question provided'}
                          </p>
                        );
                      }
                    })()}
                  </div>
                ) : (
                  <p className="patient-question-text">
                    {consultation.user_question || 'No question provided'}
                  </p>
                )}
              </div>
            </div>

            {/* AI Response & Chat History Tabs Section */}
            <div className="dashboard-section">
              <div className="section-header">
                <div className="section-title-with-icon">
                  <span className="section-icon" aria-hidden><Brain size={20} /></span>
                  <h3 className="section-title">AI Response</h3>
                </div>
              </div>
              
              {/* Tabs */}
              <div className="tabs-container">
                <div className="tabs-header">
                  <button
                    className={`tab-button ${activeTab === 'aiResponse' ? 'active' : ''}`}
                    onClick={() => setActiveTab('aiResponse')}
                  >
                    AI Response
                  </button>
                  <button
                    className={`tab-button ${activeTab === 'chatHistory' ? 'active' : ''}`}
                    onClick={() => setActiveTab('chatHistory')}
                  >
                    Chat History
                  </button>
                </div>
                
                {/* Tab Content */}
                <div className="tab-content">
                  {activeTab === 'aiResponse' && (
                    <div className="section-content">
                      {(() => {
                        // First, try to get AI response from chat messages
                        // Look for assistant messages that are comprehensive responses (not questions)
                        const aiResponses = consultation.chat_messages?.filter(
                          (msg) => {
                            if (msg.role !== 'assistant') return false;
                            const content = msg.content.toLowerCase();
                            // Exclude questions
                            if (
                              msg.content.trim().endsWith('?') ||
                              (content.includes('what') && content.includes('?')) ||
                              (content.includes('when') && content.includes('?')) ||
                              (content.includes('where') && content.includes('?')) ||
                              (content.includes('how') && content.includes('?')) ||
                              content.includes('can you') ||
                              content.includes('do you')
                            ) {
                              return false;
                            }
                            // Include comprehensive responses (longer messages or containing medical terms)
                            return msg.content.length > 100 || 
                                   content.includes('diagnosis') ||
                                   content.includes('recommend') ||
                                   content.includes('test') ||
                                   content.includes('symptom') ||
                                   content.includes('treatment');
                          }) || [];
                        
                        // Get the most comprehensive AI response (prefer longer ones)
                        const latestAiResponse = aiResponses.length > 0 
                          ? aiResponses.sort((a, b) => b.content.length - a.content.length)[0].content 
                          : null;
                        
                        // Fallback to context field, then ai_summary
                        const displayContent = latestAiResponse || 
                                             consultation.context || 
                                             consultation.ai_summary;
                        
                        if (displayContent) {
                          return (
                            <div className="ai-response-text">
                              {displayContent.split('\n').map((line, index) => (
                                <p key={index}>{line || '\u00A0'}</p>
                              ))}
                            </div>
                          );
                        } else {
                          return <p className="no-content">No AI response available</p>;
                        }
                      })()}
                    </div>
                  )}
                  
                  {activeTab === 'chatHistory' && (
                    <div className="section-content">
                      {consultation.chat_messages && consultation.chat_messages.length > 0 ? (
                        <div className="chat-history-container">
                          {consultation.chat_messages.map((msg, idx) => (
                            <div
                              key={idx}
                              className={`chat-message ${msg.role === 'user' ? 'user-message' : 'assistant-message'}`}
                            >
                              <div className="chat-message-header">
                                <span className="chat-message-role">
                                  {msg.role === 'user' ? <><User size={14} className="chat-role-icon" /> Patient</> : <><Bot size={14} className="chat-role-icon" /> Assistant</>}
                                </span>
                                {msg.created_at && (
                                  <span className="chat-message-time">
                                    {formatDateTime(msg.created_at)}
                                  </span>
                                )}
                              </div>
                              <div className="chat-message-content">
                                {msg.content.split('\n').map((line, lineIdx) => (
                                  <p key={lineIdx}>{line || '\u00A0'}</p>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="no-content">No chat history available</p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Accept/Reject Actions Section - Show only if requested */}
            {consultation.status?.toLowerCase() === 'requested' ? (
              <div className="dashboard-section">
                <div className="section-header">
                  <div className="section-title-with-icon">
                    <span className="section-icon" aria-hidden><Zap size={20} /></span>
                    <h3 className="section-title">Action Required</h3>
                  </div>
                </div>
                <div className="section-content">
                  <p className="action-required-text">
                    Please accept or reject this consultation request before proceeding.
                  </p>
                  <div className="action-buttons-group">
                    <button
                      className="action-btn-accept"
                      onClick={handleAcceptRequest}
                      disabled={processingStatus === consultation.id}
                    >
                      {processingStatus === consultation.id ? 'Processing...' : 'Accept Request'}
                    </button>
                    <button
                      className="action-btn-reject"
                      onClick={handleRejectRequest}
                      disabled={processingStatus === consultation.id}
                    >
                      {processingStatus === consultation.id ? 'Processing...' : 'Reject Request'}
                    </button>
                  </div>
                </div>
              </div>
            ) : null}

            {/* AI Medical Summary Section */}
            <div className="dashboard-section">
              <div className="section-header">
                <div className="section-title-with-icon">
                  <span className="section-icon" aria-hidden><ClipboardList size={20} /></span>
                  <h3 className="section-title">AI Medical Summary</h3>
                </div>
                {(consultation.status?.toLowerCase() === 'pending' || consultation.status?.toLowerCase() === 'completed') && 
                 !consultation.ai_summary && (
                <button
                  className="generate-summary-btn"
                  onClick={handleGenerateSummary}
                  disabled={generatingSummary}
                >
                  {generatingSummary ? 'Generating...' : 'Generate AI Summary'}
                </button>
                )}
                {consultation.ai_summary && (
                  <span className="summary-generated-badge">Summary Generated</span>
                )}
              </div>
              {(expandedSections.aiSummary || !!consultation.ai_summary) && (
                <div className="section-content">
                  {consultation.ai_summary ? (
                    <div className="medical-summary-text">
                      {consultation.ai_summary.split('\n').map((line, index) => (
                        <p key={index}>{line}</p>
                      ))}
                    </div>
                  ) : consultation.clinical_report?.report_data ? (
                    <div className="medical-summary-text">
                      <pre>{JSON.stringify(consultation.clinical_report.report_data, null, 2)}</pre>
                    </div>
                  ) : (
                    <p className="no-content">No medical summary available. Click "Generate AI Summary" to create one.</p>
                  )}
                </div>
              )}
            </div>

            {/* Doctor Response Section */}
            <div className="dashboard-section">
              <div className="section-header">
                <div className="section-title-with-icon">
                  <span className="section-icon" aria-hidden><Pill size={20} /></span>
                  <h3 className="section-title">Doctor Response</h3>
                </div>
              </div>
              <div className="section-content">
                <textarea
                  className="doctor-notes-textarea"
                  placeholder="Enter your professional medical response..."
                  value={doctorNotes}
                  onChange={(e) => setDoctorNotes(e.target.value)}
                  rows={8}
                />
                <p className="doctor-notes-hint">
                  Provide your professional medical response to the patient's questions...
                </p>
              </div>
            </div>

            {/* Drug Prescriptions Section */}
            <div className="dashboard-section">
              <div className="section-header">
                <div className="section-title-with-icon">
                  <span className="section-icon" aria-hidden><Link2 size={20} /></span>
                  <h3 className="section-title">Drug Prescriptions</h3>
                </div>
                <button
                  type="button"
                  className="add-button"
                  onClick={handleAddPrescription}
                  disabled={consultation.status?.toLowerCase() === 'completed'}
                  aria-label="Add prescription"
                >
                  +
                </button>
              </div>
              <div className="section-content">
                {prescriptions.length === 0 ? (
                  <p className="no-content">No prescriptions added. Click + to add medications.</p>
                ) : (
                  <div className="prescriptions-list">
                    {prescriptions.map((prescription) => (
                      <div key={prescription.id} className="prescription-card">
                        <div className="prescription-header">
                          <span className="prescription-label">Prescription</span>
                          <button
                            className="delete-prescription-btn"
                            onClick={() => handleDeletePrescription(prescription.id)}
                          >
                            🗑️
                          </button>
                        </div>
                        <div className="prescription-fields">
                          <input
                            type="text"
                            className="prescription-input"
                            placeholder="Medication name"
                            value={prescription.medication_name}
                            onChange={(e) => handleUpdatePrescription(prescription.id, 'medication_name', e.target.value)}
                          />
                          <div className="prescription-row">
                            <input
                              type="text"
                              className="prescription-input prescription-input-half"
                              placeholder="Dosage"
                              value={prescription.dosage}
                              onChange={(e) => handleUpdatePrescription(prescription.id, 'dosage', e.target.value)}
                            />
                            <input
                              type="text"
                              className="prescription-input prescription-input-half"
                              placeholder="Frequency"
                              value={prescription.frequency}
                              onChange={(e) => handleUpdatePrescription(prescription.id, 'frequency', e.target.value)}
                            />
                          </div>
                          <input
                            type="text"
                            className="prescription-input"
                            placeholder="Instructions"
                            value={prescription.instructions}
                            onChange={(e) => handleUpdatePrescription(prescription.id, 'instructions', e.target.value)}
                          />
                        </div>
                      </div>
                    ))}
                    <div ref={prescriptionsEndRef} />
                  </div>
                )}
              </div>
            </div>

            {/* Suggested Lab Tests Section */}
            <div className="dashboard-section">
              <div className="section-header">
                <div className="section-title-with-icon">
                  <span className="section-icon" aria-hidden><FlaskConical size={20} /></span>
                  <h3 className="section-title">Suggested Lab Tests</h3>
                </div>
                <button
                  type="button"
                  className="add-button"
                  onClick={handleAddLabTest}
                  disabled={consultation.status?.toLowerCase() === 'completed'}
                  aria-label="Add lab test"
                >
                  +
                </button>
              </div>
              <div className="section-content">
                {labTests.length === 0 ? (
                  <p className="no-content">No lab tests suggested. Click + to add test recommendations.</p>
                ) : (
                  <div className="lab-tests-list">
                    {labTests.map((labTest) => (
                      <div key={labTest.id} className="lab-test-card">
                        <div className="lab-test-header">
                          <span className="lab-test-label">Lab Test</span>
                          <button
                            className="delete-lab-test-btn"
                            onClick={() => handleDeleteLabTest(labTest.id)}
                          >
                            🗑️
                          </button>
                        </div>
                        <div className="lab-test-fields">
                          <input
                            type="text"
                            className="lab-test-input"
                            placeholder="Test name"
                            value={labTest.test_name}
                            onChange={(e) => handleUpdateLabTest(labTest.id, 'test_name', e.target.value)}
                          />
                          <input
                            type="text"
                            className="lab-test-input"
                            placeholder="Reason for test"
                            value={labTest.reason}
                            onChange={(e) => handleUpdateLabTest(labTest.id, 'reason', e.target.value)}
                          />
                          <input
                            type="text"
                            className="lab-test-input"
                            placeholder="Instructions"
                            value={labTest.instructions}
                            onChange={(e) => handleUpdateLabTest(labTest.id, 'instructions', e.target.value)}
                          />
                        </div>
                      </div>
                    ))}
                    <div ref={labTestsEndRef} />
                  </div>
                )}
              </div>
            </div>

            {/* Complete Consultation Button */}
            {consultation.status?.toLowerCase() !== 'completed' && (
            <div className="dashboard-section">
              <button
                className="complete-consultation-btn"
                onClick={handleCompleteConsultation}
              >
                <span className="complete-icon" aria-hidden><Check size={20} /></span>
                Complete Consultation
              </button>
            </div>
            )}
            {consultation.status?.toLowerCase() === 'completed' && (
              <div className="dashboard-section">
                <div className="consultation-completed-message">
                  <span className="complete-icon" aria-hidden><Check size={20} /></span>
                  Consultation Completed
                </div>
              </div>
            )}
            </div>
          </main>
        </div>
      </div>
    </div>
  );
};

export default ConsultationDetailsPage;

